// Every way a scan can end, named once; each kind of scan below takes its own outcomes
export enum ScanResultType {
    ACCEPTED = 'ACCEPTED',
    NO_TRAILER = 'NO_TRAILER',
    NOT_ENABLED = 'NOT_ENABLED',
    RECEIVED = 'RECEIVED',
    RECEIVED_AS_ORPHAN = 'RECEIVED_AS_ORPHAN',
    SIDELINED = 'SIDELINED',
    NOT_LINKED = 'NOT_LINKED',
    ALREADY_RESOLVED = 'ALREADY_RESOLVED',
    RESOLVED = 'RESOLVED',
    REFUSED = 'REFUSED',
    SYSTEM_ERROR = 'SYSTEM_ERROR'
}

// How any scan can fail, whatever was scanned: refused, or kept from an answer the app can use
export type FailureResultType = ScanResultType.REFUSED | ScanResultType.SYSTEM_ERROR;

export type DoorScanResultType = ScanResultType.ACCEPTED | ScanResultType.NO_TRAILER | ScanResultType.NOT_ENABLED
    | FailureResultType;

export type LabelScanResultType = ScanResultType.RECEIVED | ScanResultType.RECEIVED_AS_ORPHAN | ScanResultType.SIDELINED
    | FailureResultType;

export type CloseResultType = ScanResultType.ACCEPTED | FailureResultType;

// A sideline ID scanned to resolve it: accepted when it is on a current trailer or pallet
export type SidelineCheckResultType = ScanResultType.ACCEPTED | ScanResultType.ALREADY_RESOLVED
    | ScanResultType.NOT_LINKED | ScanResultType.NOT_ENABLED | FailureResultType;

// A container's own label scanned to resolve a sideline ID
export type ResolveResultType = ScanResultType.RESOLVED | FailureResultType;

// The trailer at a door, which a good door scan binds
export interface DockTrailer {
    // The dock door it is at
    doorId: string;
    // The service's key for it: its VRID, else its ISA, else its yard visit ID. Each scan sends it back as the parent,
    // and the close and the count re-read send it as the container
    trailerId: string;
    // What the screens call it: its key, or for a trailer keyed by its yard visit ID the number painted on it
    name: string;
    // Containers received onto it, as the service counts them; absent if its answer has none
    count?: number;
    // Always absent: a trailer is no pallet
    palletId?: undefined;
}

// In staging, the pallet a good pallet scan opens
export interface StagedPallet {
    // Always absent: the pallet step reads no door
    doorId?: undefined;
    // The service's key for the trailer it is linked to, which the screens show; an orphan pallet has none
    trailerId?: string;
    // Always absent: the service answers no other id for a pallet's trailer
    name?: undefined;
    // Cartons received onto it, as the service counts them; absent if its answer has none
    count?: number;
    // The pallet's label
    palletId: string;
}

// What a loop scans into: the trailer at a door, or in staging a pallet
export type Trailer = DockTrailer | StagedPallet;

export interface ScanResult<Outcome extends string> {
    // How the scan ended
    outcome: Outcome;
    // The trimmed value scanned or typed; for a close, the name of what it closed
    value: string;
    // The text the call failed with, when it had any
    message?: string;
}

export interface DoorScanResult extends ScanResult<DoorScanResultType> {
    // What an accepted scan bound
    trailer?: Trailer;
}

export type LabelScanResult = ScanResult<LabelScanResultType>;

export type CloseResult = ScanResult<CloseResultType>;

export interface SidelineCheckResult extends ScanResult<SidelineCheckResultType> {
    // What the screens call the pallet or trailer a linked sideline ID is on; absent when the answer names neither
    parentName?: string;
    // What an already resolved sideline ID was resolved to
    resolvedContainerId?: string;
}

export type ResolveResult = ScanResult<ResolveResultType>;

export interface ResolvedSideline {
    // The sideline ID resolved
    sidelineId: string;
    // The label it was resolved to
    containerId: string;
}

// What a door scan's trailer is keyed by, which GetContainer(mode=UNLOAD) answers in containerProperties.trailerIdType
export enum TrailerIdType {
    VRID = 'VRID',
    ISA = 'ISA',
    VISIT = 'VISIT'
}

// The values GetContainer(mode=UNLOAD) answers in containerProperties.unloadScanOutcome: a dock door answers one of
// the first three, a pallet UNLOAD_NOT_ENABLED or a PALLET_ one, and a sideline ID UNLOAD_NOT_ENABLED or a SIDELINE_ one
export enum UnloadScanOutcome {
    OK = 'OK',
    NO_TRAILER_AT_DOOR = 'NO_TRAILER_AT_DOOR',
    UNLOAD_NOT_ENABLED = 'UNLOAD_NOT_ENABLED',
    PALLET_LINKED = 'PALLET_LINKED',
    PALLET_ORPHAN = 'PALLET_ORPHAN',
    SIDELINE_LINKED = 'SIDELINE_LINKED',
    SIDELINE_RESOLVED = 'SIDELINE_RESOLVED',
    SIDELINE_NOT_LINKED = 'SIDELINE_NOT_LINKED'
}

// The tool the BFF lists, by this name, for an associate FCLM gives Expert or above for RIV: the problem solvers, who
// alone resolve sideline IDs
export const RESOLVE_SIDELINE_TOOL = 'ResolveSideline';
